// The view half of the exercise registry: how the lesson shell renders a step.
// Kept out of registry.ts so the logic stays React-free for headless consumers
// (the validator, the studio, a native app registering its own views).
import type { ProgressKey } from "@aybuben/core/content/types.ts";
import type { LessonStep } from "@aybuben/core/lesson.ts";
import type { ReactNode } from "react";

export interface ExerciseViewProps<S extends LessonStep = LessonStep, A = string> {
	step: S;
	/** What the learner has entered so far; null until they touch anything. */
	answer: A | null;
	/**
	 * `A | null`, not `A`: a view must be able to say "no longer answered".
	 * The shell disables Check on `answer === null`, and every founding type is
	 * a single tap that only ever *becomes* answered — but a compose learner who
	 * fills every slot and then takes a chip out is not answered any more. The
	 * alternatives are worse: enabling Check on an incomplete sequence makes
	 * pressing it a counted mistake, and forbidding removal deletes a legitimate
	 * affordance.
	 */
	setAnswer: (a: A | null) => void;
	/** Set once the step has been graded; null while it is still answerable. */
	status: "right" | "wrong" | null;
	/** True when this step is being re-asked after an earlier wrong answer. */
	retry: boolean;
	/** For a step that completes itself (match) rather than being checked. */
	onComplete: () => void;
	/** For a step that scores its own sub-answers (match pairs). */
	/** `picked` names a miss's pick, for analytics. */
	onScore: (key: ProgressKey, ok: boolean, picked?: string) => void;
	/**
	 * Enter in a typed answer: what the action button would do now, Check
	 * while unanswered, Continue once graded.
	 */
	onSubmit: () => void;
}

export interface FeedbackProps<S extends LessonStep = LessonStep, A = unknown> {
	step: S;
	ok: boolean;
	/** What was checked: a typed answer's feedback shows the learner's own text. */
	answer?: A | null;
}

/** What the shell puts in the action slot while the step is unanswered. */
type ActionKind = "check" | "continue" | "none";

export interface ExerciseView<S extends LessonStep = LessonStep, A = string> {
	View: (p: ExerciseViewProps<S, A>) => ReactNode;
	/** The post-answer panel. Absent for a type that is never graded. */
	Feedback?: (p: FeedbackProps<S, A>) => ReactNode;
	action: ActionKind;
	/** Rendered in the action slot when `action` is "none". */
	Footer?: () => ReactNode;
}

export type ExerciseViewApi = ExerciseView<LessonStep, unknown>;

const views = new Map<string, ExerciseViewApi>();

export function registerView<S extends LessonStep, A>(id: string, v: ExerciseView<S, A>): void {
	if (views.has(id)) throw new Error(`Exercise view already registered: ${id}`);
	// Widened for the same reason as register()'s cast above, and safe for the
	// same reason: the shell looks a view up by `step.kind`, which equals the
	// id of the type that built the step, so a view only ever receives a step
	// its own type produced — and only ever an answer its own View set.
	views.set(id, v as unknown as ExerciseViewApi);
}

export function exerciseView(id: string): ExerciseViewApi {
	const v = views.get(id);
	// Reachable only if exercises/views.ts was not imported, or a type
	// registered logic without a view. barrels.test.ts pins the second case.
	if (!v) throw new Error(`No view registered for exercise type: ${id}`);
	return v;
}

export const allExerciseViewIds = (): string[] => [...views.keys()];
